import { Dispatch, createSlice } from "@reduxjs/toolkit";
import { TRequestStatus } from "@models/msc.model";
import qService from "@/services/questionnaires.service";
import { TQuestionnaire, TQuestionnaireDisqualification, TQuestionnairePayload } from "@/models/questionnaires.model";
import { RootState } from "..";
import { TRouting } from "@/models/conditional-routing.model";

type QuestionnaireState = {
  initialized: boolean;
  status: TRequestStatus;
  questionnaires: TQuestionnaire[];
};

//-----THUNK FUNCTIONS-----

const initialState: QuestionnaireState = {
  initialized: false,
  status: "IDLE",
  questionnaires: [],
};

const questionnairesSlice = createSlice({
  name: "questionnaires",
  initialState,
  reducers: {
    startLoading(state) {
      state.status = "PENDING";
    },
    gotQuestionnaires(state, action) {
      state.questionnaires = action.payload;
      state.status = "IDLE";
      state.initialized = true;
    },
    questionnaireUpdated(state, action) {
      const { payload } = action;
      state.questionnaires = state.questionnaires.map((q) => (q.id === payload.id ? payload : q));
    },
    calendarAttachedToQuestionnaire(state, action) {
      const { questionnaireId, calendar } = action.payload;
      state.questionnaires = state.questionnaires.map((q) => (q.id === questionnaireId ? { ...q, calendar } : q));
    },
    calendarRemovedFromQuestionnaire(state, action) {
      const { questionnaireId } = action.payload;
      state.questionnaires = state.questionnaires.map((q) =>
        q.id === questionnaireId ? { ...q, calendar: undefined } : q
      );
    },
    questionnaireSaved(state, action) {
      state.questionnaires = [action.payload, ...state.questionnaires];
    },
    questionnaireDeleted(state, action) {
      const id = action.payload;
      state.questionnaires = state.questionnaires.filter((q) => q.id !== id);
    },
    disqualificationSaved(state, action) {
      const { disqualifications, questionnaireId } = action.payload;
      state.questionnaires = state.questionnaires.map((q) =>
        q.id === questionnaireId ? { ...q, disqualifications } : q
      );
    },
    conditionRoutingSaved(state, action) {
      const { conditionalRoutings, questionnaireId } = action.payload;
      state.questionnaires = state.questionnaires.map((q) =>
        q.id === questionnaireId ? { ...q, conditionalRoutings } : q
      );
    },
  },
});

export const { calendarAttachedToQuestionnaire, calendarRemovedFromQuestionnaire } = questionnairesSlice.actions;

// ----------------------------

export const getQuestionnaires = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const {
    questionnaires: { questionnaires, initialized },
  } = getState();

  if (initialized) return questionnaires;

  dispatch(questionnairesSlice.actions.startLoading());
  const response = await qService.getAll();
  dispatch(questionnairesSlice.actions.gotQuestionnaires(response));
  return response;
};

export const updateQuestionnaire =
  (payload: { id: number; data: TQuestionnairePayload }) => async (dispatch: Dispatch) => {
    const response = await qService.update(payload);
    dispatch(questionnairesSlice.actions.questionnaireUpdated(response));
    return response;
  };

export default questionnairesSlice.reducer;

//** actions */

export const saveQuestionnaire = (data: TQuestionnairePayload) => async (dispatch: Dispatch) => {
  const questionAdded = await qService.save(data);
  dispatch(questionnairesSlice.actions.questionnaireSaved(questionAdded));
  return questionAdded;
};

export const deleteQuestionnaire = (id: number) => async (dispatch: Dispatch) => {
  await qService.deleteById(id);
  dispatch(questionnairesSlice.actions.questionnaireDeleted(id));
};

export const updateQuestionnaireDisqualification =
  (data: { questionnaireId: number; disqualifications: TQuestionnaireDisqualification[] }) =>
    async (dispatch: Dispatch) => {
      const response = await qService.saveDisqualificationService(data);
      dispatch(
        questionnairesSlice.actions.disqualificationSaved({
          questionnaireId: data.questionnaireId,
          disqualifications: response,
        })
      );
      return response;

    };

export const updateQuestionnaireConditionRouting =
  (data: { questionnaireId: number; conditionalRoutings: TRouting[] }) => async (dispatch: Dispatch) => {
    const response = await qService.saveConditionalRoutingService(data);
    dispatch(
      questionnairesSlice.actions.conditionRoutingSaved({
        questionnaireId: data.questionnaireId,
        conditionalRoutings: response,
      })
    );
    return response;

  };
